import { useRef, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Trash2, X, GalleryHorizontal, Grid3x3 } from "lucide-react";
import { api } from "@/lib/api";
import {
  selfieCheckpointForDay,
  selfieDayLabel,
  SELFIE_COUNT,
} from "@/lib/skincare";
import { celebrate } from "@/lib/fx";

const ANGLES = [
  { id: "front", label: "Front" },
  { id: "left",  label: "Left"  },
  { id: "right", label: "Right" },
];

export default function SelfiesView({ photos, setPhotos, dayNum }) {
  const fileRef = useRef(null);
  const [uploading, setUploading]     = useState(false);
  const [viewing, setViewing]         = useState(null); // { cp, angle }
  const [mode, setMode]               = useState("grid");
  const [uploadCp, setUploadCp]       = useState(0);
  const [uploadAngle, setUploadAngle] = useState("front");
  const [sliderAngle, setSliderAngle] = useState("front");

  const currentCheckpoint = selfieCheckpointForDay(Math.max(1, dayNum));
  const checkpoints = [0, ...Array.from({ length: SELFIE_COUNT }, (_, i) => i + 1)];

  // byCp: { [week]: { [angle]: photo } }  — backward compat: missing angle → "front"
  const byCp = useMemo(() => {
    const m = {};
    photos.forEach((p) => {
      const angle = p.angle || "front";
      if (!m[p.week]) m[p.week] = {};
      m[p.week][angle] = p;
    });
    return m;
  }, [photos]);

  const [sliderIdx, setSliderIdx] = useState(() => {
    const taken = checkpoints.filter((c) => byCp[c] && Object.keys(byCp[c]).length > 0);
    return taken.length ? taken[taken.length - 1] : 0;
  });

  // Primary photo for a checkpoint (front → first taken → null)
  const primaryPhoto = (cp) =>
    byCp[cp]?.front || Object.values(byCp[cp] || {})[0] || null;

  const angleTaken = (cp) => Object.keys(byCp[cp] || {}).length;

  const triggerUpload = (cp, angle) => {
    setUploadCp(cp);
    setUploadAngle(angle);
    setTimeout(() => fileRef.current?.click(), 0);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      await api.savePhoto({
        week: uploadCp,
        angle: uploadAngle,
        image_base64: dataUrl,
        taken_at: new Date().toISOString(),
      });
      const fresh = await api.listPhotos();
      setPhotos(fresh);
      celebrate("default");
    } catch (err) {
      console.error("Photo upload failed:", err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (cp, angle) => {
    await api.deletePhoto(cp, angle);
    const fresh = await api.listPhotos();
    setPhotos(fresh);
    setViewing(null);
  };

  // Three angle camera buttons used inside CTAs
  const AngleButtons = ({ cp }) => (
    <div className="flex gap-2 mt-3">
      {ANGLES.map((a) => {
        const photo = byCp[cp]?.[a.id];
        return (
          <button
            key={a.id}
            onClick={() => triggerUpload(cp, a.id)}
            disabled={uploading}
            className={`flex-1 flex flex-col items-center gap-1.5 rounded-xl border p-2 transition-colors disabled:opacity-50 ${
              photo ? "border-[#4A6B53] bg-[#E9F0E8]" : "border-[#E9EFEA] bg-white"
            }`}
          >
            {photo ? (
              <div className="w-10 h-10 rounded-lg overflow-hidden">
                <img src={photo.image_base64} alt={a.label} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-lg bg-[#F0EFE7] flex items-center justify-center">
                <Camera className="w-4 h-4 text-[#6B7A70]" strokeWidth={1.5} />
              </div>
            )}
            <span className={`text-[10px] font-medium ${photo ? "text-[#4A6B53]" : "text-[#6B7A70]"}`}>
              {a.label}
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="px-6 pt-8 pb-10" data-testid="selfies-view">
      <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#6B7A70]">
        Progress Selfies · Day 1 + every 5 days
      </div>
      <h2 className="font-heading text-3xl font-semibold text-[#2A332C] tracking-tight mt-1">
        Front, left, right. 3 angles. 1 transformation.
      </h2>
      <p className="text-sm text-[#6B7A70] mt-2">
        Capture all 3 angles at each checkpoint. Same lighting, same spot. Watch your skin evolve.
      </p>

      {/* Day 1 before-photo CTA */}
      {angleTaken(0) < 3 && (
        <div className="mt-6 p-5 rounded-3xl bg-gradient-to-br from-[#FFF8F0] to-[#FADBD8]/40 border border-[#F5CBA7]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center shrink-0">
              <Camera className="w-4 h-4 text-[#B07A3E]" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-heading font-medium text-[#2A332C] text-sm">Day 1 · Before photos</div>
              <div className="text-xs text-[#6B7A70]">{angleTaken(0)}/3 angles taken</div>
            </div>
          </div>
          <AngleButtons cp={0} />
        </div>
      )}

      {/* Current checkpoint CTA */}
      {currentCheckpoint > 0 && currentCheckpoint <= SELFIE_COUNT && (
        <div className="mt-3 p-5 rounded-3xl bg-gradient-to-br from-[#E9F0E8] to-[#FADBD8]/30 border border-[#CDE0D1]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center shrink-0">
              <Camera className="w-4 h-4 text-[#4A6B53]" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-heading font-medium text-[#2A332C] text-sm">
                Day {selfieDayLabel(currentCheckpoint)} · Checkpoint {currentCheckpoint}
              </div>
              <div className="text-xs text-[#6B7A70]">{angleTaken(currentCheckpoint)}/3 angles taken</div>
            </div>
          </div>
          <AngleButtons cp={currentCheckpoint} />
        </div>
      )}

      {/* Hidden file input — no capture attr so user can pick camera or gallery */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
      />

      {/* Mode toggle */}
      <div className="mt-6 inline-flex p-1 bg-white border border-[#E9EFEA] rounded-full">
        <button
          data-testid="mode-grid"
          onClick={() => setMode("grid")}
          className={`px-4 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${
            mode === "grid" ? "bg-[#2A332C] text-white" : "text-[#6B7A70]"
          }`}
        >
          <Grid3x3 className="w-3.5 h-3.5" strokeWidth={1.8} /> Grid
        </button>
        <button
          data-testid="mode-slider"
          onClick={() => setMode("slider")}
          className={`px-4 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${
            mode === "slider" ? "bg-[#2A332C] text-white" : "text-[#6B7A70]"
          }`}
        >
          <GalleryHorizontal className="w-3.5 h-3.5" strokeWidth={1.8} /> Timeline
        </button>
      </div>

      {mode === "grid" && (
        <div className="grid grid-cols-3 gap-3 mt-4" data-testid="photos-grid">
          {checkpoints.map((cp) => {
            const primary = primaryPhoto(cp);
            const taken   = angleTaken(cp);
            const isCurrent = cp === currentCheckpoint;
            const isDay1    = cp === 0;
            return (
              <button
                key={cp}
                onClick={() =>
                  primary
                    ? setViewing({ cp, angle: primary.angle || "front" })
                    : triggerUpload(cp, "front")
                }
                className={`aspect-square rounded-2xl overflow-hidden relative border ${
                  isDay1 && !primary
                    ? "border-[#F5CBA7] border-2"
                    : isCurrent
                    ? "border-[#4A6B53] border-2"
                    : "border-[#E9EFEA]"
                } ${primary ? "" : "bg-white/60"}`}
              >
                {primary ? (
                  <img
                    src={primary.image_base64}
                    alt={`Day ${selfieDayLabel(cp)}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-[#6B7A70]">
                    <Camera className="w-4 h-4 mb-1" strokeWidth={1.5} />
                    <span className="text-[10px] font-medium">
                      {isDay1 ? "Before" : `Day ${selfieDayLabel(cp)}`}
                    </span>
                  </div>
                )}
                {/* Angle dot indicators */}
                <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-1">
                  {ANGLES.map((a) => (
                    <div
                      key={a.id}
                      className={`w-1.5 h-1.5 rounded-full ${
                        byCp[cp]?.[a.id] ? "bg-[#4A6B53]" : "bg-white/40"
                      }`}
                    />
                  ))}
                </div>
                {taken > 0 && (
                  <div className="absolute top-1 right-1 bg-black/50 text-white text-[9px] px-1.5 py-0.5 rounded-full font-semibold">
                    {taken}/3
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {mode === "slider" && (
        <div className="mt-4" data-testid="photos-slider">
          {/* Angle selector */}
          <div className="flex gap-2 mb-3">
            {ANGLES.map((a) => (
              <button
                key={a.id}
                onClick={() => setSliderAngle(a.id)}
                className={`flex-1 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  sliderAngle === a.id
                    ? "bg-[#2A332C] text-white border-[#2A332C]"
                    : "bg-white text-[#6B7A70] border-[#E9EFEA]"
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>

          <div className="relative aspect-square rounded-3xl overflow-hidden bg-[#F0EFE7] border border-[#E9EFEA]">
            <AnimatePresence mode="wait">
              {byCp[sliderIdx]?.[sliderAngle] ? (
                <motion.img
                  key={`${sliderIdx}-${sliderAngle}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  src={byCp[sliderIdx][sliderAngle].image_base64}
                  alt={`Day ${selfieDayLabel(sliderIdx)} ${sliderAngle}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <motion.div
                  key={`empty-${sliderIdx}-${sliderAngle}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute inset-0 flex flex-col items-center justify-center text-[#6B7A70]"
                >
                  <Camera className="w-8 h-8 mb-2" strokeWidth={1.2} />
                  <div className="font-heading text-base">No {sliderAngle} photo</div>
                  <div className="text-xs mt-1">Day {selfieDayLabel(sliderIdx)}</div>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-black/55 text-white text-xs font-semibold">
              Day {selfieDayLabel(sliderIdx)}
            </div>
          </div>

          <input
            type="range"
            min={0}
            max={SELFIE_COUNT}
            value={sliderIdx}
            onChange={(e) => setSliderIdx(Number(e.target.value))}
            className="w-full mt-5 accent-[#4A6B53]"
            data-testid="timeline-range"
          />
          <div className="flex justify-between text-[10px] text-[#6B7A70] mt-1 px-0.5">
            {checkpoints.map((cp) => (
              <span
                key={cp}
                className={`${
                  primaryPhoto(cp) ? "text-[#4A6B53] font-semibold" : ""
                } ${cp === sliderIdx ? "text-[#2A332C] font-bold" : ""}`}
              >
                {selfieDayLabel(cp)}
              </span>
            ))}
          </div>
          <p className="text-center text-[11px] text-[#6B7A70] mt-3">
            Drag the slider to scrub through your transformation.
          </p>
        </div>
      )}

      {/* Viewing modal with angle tabs */}
      {viewing && (
        <div
          className="fixed inset-0 bg-black/85 z-50 flex flex-col items-center justify-center p-4"
          onClick={() => setViewing(null)}
        >
          <button
            className="absolute top-4 right-4 text-white p-2"
            onClick={() => setViewing(null)}
          >
            <X />
          </button>
          <div className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            {/* Angle tabs */}
            <div className="flex gap-2 mb-3 justify-center">
              {ANGLES.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setViewing((v) => ({ ...v, angle: a.id }))}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    viewing.angle === a.id
                      ? "bg-white text-[#2A332C]"
                      : byCp[viewing.cp]?.[a.id]
                      ? "bg-white/20 text-white"
                      : "bg-white/10 text-white/40"
                  }`}
                >
                  {a.label}
                </button>
              ))}
            </div>

            {byCp[viewing.cp]?.[viewing.angle] ? (
              <>
                <img
                  src={byCp[viewing.cp][viewing.angle].image_base64}
                  alt={viewing.angle}
                  className="w-full rounded-2xl"
                />
                <div className="mt-4 flex items-center justify-between text-white">
                  <div>
                    <div className="font-heading">
                      {viewing.cp === 0 ? "Day 1 · Before" : `Day ${selfieDayLabel(viewing.cp)}`}
                      {" · "}{viewing.angle}
                    </div>
                    {(byCp[viewing.cp][viewing.angle].taken_at || byCp[viewing.cp][viewing.angle].created_at) && (
                      <div className="text-xs text-white/70 mt-0.5">
                        {new Date(
                          byCp[viewing.cp][viewing.angle].taken_at ||
                          byCp[viewing.cp][viewing.angle].created_at
                        ).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setViewing(null); triggerUpload(viewing.cp, viewing.angle); }}
                      className="flex items-center gap-1.5 text-sm bg-white/10 px-3 py-1.5 rounded-full"
                    >
                      <Camera className="w-3.5 h-3.5" /> Retake
                    </button>
                    <button
                      onClick={() => remove(viewing.cp, viewing.angle)}
                      className="flex items-center gap-1.5 text-sm bg-white/10 px-3 py-1.5 rounded-full"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="aspect-square rounded-2xl bg-white/10 flex flex-col items-center justify-center text-white/60">
                <Camera className="w-10 h-10 mb-2" strokeWidth={1.2} />
                <div>No {viewing.angle} photo yet</div>
                <button
                  onClick={() => { setViewing(null); triggerUpload(viewing.cp, viewing.angle); }}
                  className="mt-4 px-5 py-2 bg-white text-[#2A332C] rounded-full text-sm font-medium"
                >
                  Add {viewing.angle} photo
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
